import { useState } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

/**
 * Admin Login Page - Professional Standard
 * Clean, centered authentication card. Admin accounts require a
 * follow-up email verification code (2FA) after a correct password.
 */
export default function Login() {
    const navigate = useNavigate()
    const location = useLocation()
    const { login, verifyTwoFactor, error, loading } = useAuth()
    const [credentials, setCredentials] = useState({ email: '', password: '' })
    const [step, setStep] = useState('credentials') // 'credentials' | 'code'
    const [code, setCode] = useState('')
    const [showPassword, setShowPassword] = useState(false)

    const goToDashboard = () => navigate(location.state?.from?.pathname || '/dashboard', { replace: true })

    const handleCredentialsSubmit = async (e) => {
        e.preventDefault()
        const result = await login(credentials.email, credentials.password)
        if (result === 'success') goToDashboard()
        else if (result === '2fa') setStep('code')
    }

    const handleCodeSubmit = async (e) => {
        e.preventDefault()
        if (await verifyTwoFactor(credentials.email, code)) goToDashboard()
    }

    return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
            <div className="w-full max-w-sm bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
                <div className="p-8 text-center bg-brand-navy border-b border-brand-navy-dark">
                    <img src="/logo.png" alt="TenderCentriX" className="h-10 mx-auto mb-4" />
                    <h1 className="text-lg font-bold text-white">Admin Portal</h1>
                    <p className="text-sm text-slate-300 mt-1">
                        {step === 'code' ? 'Enter the verification code emailed to you' : 'Login to manage your platform'}
                    </p>
                </div>

                {step === 'credentials' ? (
                    <form onSubmit={handleCredentialsSubmit} className="p-8 space-y-5">
                        {error && (
                            <div className="p-3 bg-red-50 text-red-600 text-sm rounded border border-red-100">
                                {error}
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                            <input
                                type="email"
                                required
                                className="w-full px-4 py-3 text-sm font-medium border text-slate-900 truncate rounded-lg focus:border-brand-navy focus:ring-1 focus:ring-brand-navy outline-none placeholder:text-slate-400 shadow-sm"
                                placeholder="admin@company.com"
                                value={credentials.email}
                                onChange={e => setCredentials({ ...credentials, email: e.target.value })}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    required
                                    autoComplete="current-password"
                                    className="w-full pl-4 pr-12 py-3 text-sm font-medium border text-slate-900 truncate rounded-lg focus:border-brand-navy focus:ring-1 focus:ring-brand-navy outline-none placeholder:text-slate-400 shadow-sm"
                                    placeholder="••••••••"
                                    value={credentials.password}
                                    onChange={e => setCredentials({ ...credentials, password: e.target.value })}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword ? (
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-gradient-to-r from-[#a91e2d] to-[#d43f52] hover:bg-gradient-to-l focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#a91e2d] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            {loading && (
                                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            )}
                            {loading ? 'Logging In...' : 'Login'}
                        </button>

                        <div className="text-center">
                            <Link to="/forgot-password" className="text-xs text-slate-400 hover:text-slate-600">Forgot password?</Link>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={handleCodeSubmit} className="p-8 space-y-5">
                        {error && (
                            <div className="p-3 bg-red-50 text-red-600 text-sm rounded border border-red-100">
                                {error}
                            </div>
                        )}

                        <p className="text-sm text-slate-600">
                            We sent a 6-digit code to <strong>{credentials.email}</strong>. It expires in 10 minutes.
                        </p>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Verification code</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]{6}"
                                maxLength={6}
                                required
                                autoFocus
                                className="w-full px-4 py-3 text-center text-lg font-bold tracking-[0.5em] border text-slate-900 rounded-lg focus:border-brand-navy focus:ring-1 focus:ring-brand-navy outline-none placeholder:text-slate-400 shadow-sm"
                                placeholder="000000"
                                value={code}
                                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading || code.length !== 6}
                            className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-gradient-to-r from-[#a91e2d] to-[#d43f52] hover:bg-gradient-to-l focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#a91e2d] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            {loading ? 'Verifying...' : 'Verify & Continue'}
                        </button>

                        <div className="text-center">
                            <button
                                type="button"
                                onClick={() => { setStep('credentials'); setCode('') }}
                                className="text-xs text-slate-400 hover:text-slate-600"
                            >
                                Back to login
                            </button>
                        </div>
                    </form>
                )}

                <div className="bg-slate-50 p-4 text-center border-t border-slate-100">
                    <p className="text-xs text-slate-400">© 2026 TenderCentriX. Secure Logged Access.</p>
                </div>
            </div>
        </div>
    )
}
